import { useCallback, useEffect, useState } from "react";
import { useUrlParams } from "./use-url-params";
import { useApi } from "./use-api";
import { useUrlSetter } from "./use-url-sync";

export interface EventType {
  value: string; // term_id
  label: string; // name
}

/**
 * Convert event type label to URL-friendly slug
 */
const labelToSlug = (label: string): string => {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim();
};

/**
 * Hook to manage event type synchronization with URL parameters
 */
export const useEventTypeUrl = () => {
  const { getParam, setParam, removeParam } = useUrlParams();
  const { fetchEventTypes } = useApi();
  
  const [eventTypes, setEventTypes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentEventType, setCurrentEventType] = useState<EventType | undefined>();

  // Load event types from API
  useEffect(() => {
    const loadEventTypes = async () => {
      try {
        setIsLoading(true);
        const results = await fetchEventTypes();
        setEventTypes(results);
      } catch (error) {
        console.error('Failed to fetch event types:', error);
        setEventTypes([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadEventTypes();
  }, [fetchEventTypes]);

  // Initialize event type from URL when event types are loaded
  useEffect(() => {
    if (isLoading || eventTypes.length === 0) return;

    const urlEventType = getParam('event_type');
    if (!urlEventType) {
      setCurrentEventType(undefined);
      return;
    }

    // Find matching event type by comparing slugified labels
    const matchingEventType = eventTypes.find(eventType => 
      labelToSlug(eventType.name) === urlEventType
    );

    if (matchingEventType) {
      setCurrentEventType({
        value: matchingEventType.term_id,
        label: matchingEventType.name
      });
    } else {
      // Invalid URL parameter, remove it
      removeParam('event_type');
      setCurrentEventType(undefined);
    }
  }, [eventTypes, isLoading, getParam, removeParam]);

  /**
   * Update event type and sync with URL
   */
  const syncEventTypeToUrl = useUrlSetter<EventType>(
    'event_type',
    setParam,
    removeParam,
    (eventType: EventType) => labelToSlug(eventType.label),
  );

  const setEventType = useCallback((eventType: EventType | undefined) => {
    setCurrentEventType(eventType);
    syncEventTypeToUrl(eventType);
  }, [syncEventTypeToUrl]);

  /**
   * Get event type from value (term_id)
   */
  const getEventTypeByValue = useCallback((value: string): EventType | undefined => {
    const eventType = eventTypes.find(et => et.term_id === value);
    return eventType ? {
      value: eventType.term_id,
      label: eventType.name
    } : undefined;
  }, [eventTypes]);

  return {
    currentEventType,
    setEventType,
    eventTypes,
    isLoading,
    getEventTypeByValue,
  };
};